import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { FileText, Download, FileSignature, AlertTriangle, CheckCircle, Upload, Eye, LayoutDashboard, Receipt } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DriverDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [recentStatements, setRecentStatements] = useState([]);
  const [myDocs, setMyDocs] = useState({});
  const [myDriverId, setMyDriverId] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    fetchMyDocs();
  }, []);

  const fetchMyDocs = async () => {
    try {
      const res = await axios.get(`${API_URL}/driver/my-documents`);
      setMyDocs(res.data.documents || {});
      setMyDriverId(res.data.driver_id);
    } catch (e) {
      console.error('Failed to load documents');
    }
  };

  const handleUpload = async (docType, file) => {
    setUploadingDoc(docType);
    const formData = new FormData();
    formData.append('doc_type', docType);
    formData.append('file', file);
    try {
      await axios.post(`${API_URL}/driver/my-documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded!');
      fetchMyDocs();
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingDoc(null);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [statsRes, contractsRes, statementsRes] = await Promise.all([
        axios.get(`${API_URL}/statements/stats`),
        axios.get(`${API_URL}/contracts`),
        axios.get(`${API_URL}/statements`)
      ]);
      
      setStats(statsRes.data);
      setContracts(contractsRes.data);
      setRecentStatements(statementsRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const pendingContracts = contracts.filter(c => c.status === 'pending');

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="driver-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Welcome back, {user?.full_name?.split(' ')[0]}</h1>
          <p className="text-muted-foreground mt-1">Here's an overview of your account</p>
        </div>

        {/* Pending Contract Alert */}
        {pendingContracts.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-900">Contract Pending Signature</p>
                  <p className="text-sm text-amber-700">You have {pendingContracts.length} contract(s) awaiting your signature</p>
                </div>
              </div>
              <Link to="/driver/contracts">
                <Button variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-100">
                  View Contracts
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid removed — driver overview intentionally hides all numbers on login */}

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid grid-cols-2 w-full h-auto" data-testid="driver-tabs">
            <TabsTrigger value="overview" className="gap-2 py-3" data-testid="tab-overview">
              <LayoutDashboard className="h-4 w-4" /> Overview
            </TabsTrigger>
            <TabsTrigger value="statements" className="gap-2 py-3" data-testid="tab-statements">
              <Receipt className="h-4 w-4" /> Statements & Payslips
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6 pt-4">
        {/* My Documents - Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-emerald-600" />
              My Documents
            </CardTitle>
            <CardDescription>Upload your required documents (PDF or photo)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { key: 'driver_id', label: 'Driver ID Copy' },
                { key: 'drivers_license', label: 'Drivers License' },
                { key: 'owner_id', label: 'Owner ID Copy' },
                { key: 'enatis', label: 'eNatis Document' },
                { key: 'vehicle_images', label: 'Vehicle Images' },
                { key: 'tracker_certificate', label: 'Tracker Certificate' },
                { key: 'license_disc', label: 'License Disc' },
                { key: 'page_28', label: 'Page 28' },
                { key: 'criminal_checks', label: 'Criminal Checks' }
              ].map(doc => {
                const docInfo = myDocs[doc.key] || {};
                const hasFile = docInfo.has_file;
                return (
                  <div key={doc.key} className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      <FileText className={`h-5 w-5 ${hasFile ? 'text-emerald-600' : 'text-slate-400'}`} />
                      <div>
                        <p className="font-medium text-sm">{doc.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {hasFile ? `Uploaded: ${docInfo.filename}` : (docInfo.status && docInfo.status !== 'No' ? docInfo.status : 'Not uploaded yet')}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {hasFile && (
                        <a
                          href={`${API_URL}${docInfo.url}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Button variant="outline" size="sm" className="text-blue-600 border-blue-300">
                            <Eye className="h-3.5 w-3.5 mr-1" /> View
                          </Button>
                        </a>
                      )}
                      <label>
                        <Button
                          variant={hasFile ? "outline" : "default"}
                          size="sm"
                          className={hasFile ? "text-emerald-600 border-emerald-300" : "bg-emerald-600 hover:bg-emerald-700"}
                          disabled={uploadingDoc === doc.key}
                          asChild
                        >
                          <span>
                            <Upload className="h-3.5 w-3.5 mr-1" />
                            {uploadingDoc === doc.key ? 'Uploading...' : (hasFile ? 'Replace' : 'Upload')}
                          </span>
                        </Button>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
                          disabled={uploadingDoc === doc.key}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleUpload(doc.key, file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recent Statements */}
          </TabsContent>

          <TabsContent value="statements" className="space-y-6 pt-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent Statements</CardTitle>
              <CardDescription>Your latest pay statements</CardDescription>
            </div>
            <Link to="/driver/statements">
              <Button variant="outline" size="sm">View All</Button>
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : recentStatements.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No statements available yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentStatements.map((statement) => (
                  <div 
                    key={statement.id}
                    className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{statement.period_start} - {statement.period_end}</p>
                        <p className="text-sm text-muted-foreground">
                          {statement.pay_run_reference || 'Pay Statement'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {statement.amount && (
                        <span className="font-semibold">R{statement.amount.toLocaleString()}</span>
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => window.open(`${API_URL}/statements/${statement.id}/download`, '_blank')}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="card-interactive">
            <Link to="/driver/statements">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
                  <FileText className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold">View All Statements</h3>
                  <p className="text-sm text-muted-foreground">Browse and download your pay statements</p>
                </div>
              </CardContent>
            </Link>
          </Card>

          <Card className="card-interactive">
            <Link to="/driver/contracts">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-accent flex items-center justify-center">
                  <FileSignature className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">Manage Contracts</h3>
                  <p className="text-sm text-muted-foreground">View and sign your service contracts</p>
                </div>
              </CardContent>
            </Link>
          </Card>
        </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
